import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

import {
    Box,
    BottomNavigation,
    BottomNavigationAction,
    Chip,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@mui/material';

import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Dashboard as DashboardIcon,
    Devices as DevicesIcon,
    ExitToApp,
    House as HouseIcon,
    Login as LoginIcon,
    LogoutOutlined as LogoutOutlinedIcon,
    Menu as MenuIcon,
    Monitor as MonitorIcon,
    RocketLaunch as RocketLaunchIcon,
    Android as AndroidIcon,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';

import FooterContent from './FooterContent';
import MenuButton from './MenuButton';
import Main from './Main';
import AppBar from './AppBar';
import Footer from './Footer';
import DrawerHeader from './DrawerHeader';
import GetStartedModal from '../GetStartedModal';

import { getAllHouses } from '../../api_calls/api_calls_houses';
import { useUserContext } from '../../context/UserContext';
import { useHouseDetails } from '../../context/HouseDetailsContext';
import axios from 'axios';


const drawerWidth = 240;

// Custom hook for logout and redirect
function useLogoutAndRedirect(logout, navigate, lastMenuItemsRef, setHouses, setSelectedHouseId) {
    return useCallback(() => {
        logout();
        lastMenuItemsRef.current = []; // Clear the cached menu
        setHouses([]);
        setSelectedHouseId('');
        localStorage.removeItem('selectedHouseId'); // Clear stored selection on logout
        navigate('/');
    }, [logout, navigate, lastMenuItemsRef, setHouses, setSelectedHouseId]);
}

// SidebarMenu component (refactored):
function SidebarMenu({ menuItems, houses, location, handleClickMenu, token, handleSignOut, selectedHouseId, setSelectedHouseId, theme }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            <List>
                {/* House dropdown at the top if houses exist */}
                {houses && houses.length > 0 && (
                    <ListItem>
                        <Select
                            value={selectedHouseId}
                            onChange={e => {
                                const newHouseId = e.target.value;
                                setSelectedHouseId(newHouseId);
                                localStorage.setItem('selectedHouseId', newHouseId);
                            }}
                            fullWidth
                            size="small"
                            input={<OutlinedInput notched={false} sx={{ borderRadius: 2, fontWeight: 600, bgcolor: 'background.paper', boxShadow: 1, px: 1.5, py: 0.5 }} />}
                            renderValue={selected => {
                                const house = houses.find(h => h.id === selected);
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <HouseIcon color="primary" sx={{ fontSize: 22 }} />
                                        <Typography fontWeight={700} sx={{ color: 'text.primary' }}>
                                            {house ? house.name : ''}
                                        </Typography>
                                    </Box>
                                );
                            }}
                            sx={{ fontWeight: 600, borderRadius: 2, boxShadow: 1, bgcolor: 'background.paper', mb: 1 }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: 2,
                                        boxShadow: 1,
                                        mt: 1,
                                    }
                                }
                            }}
                        >
                            {houses.map(house => (
                                <MenuItem key={house.id} value={house.id} sx={{ py: 1.2, px: 2, borderRadius: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <HouseIcon color="primary" sx={{ fontSize: 20, mr: 1 }} />
                                        <Typography fontWeight={600}>{house.name}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </ListItem>
                )}
                {menuItems.map(menuItem => {
                    const isSelected = menuItem.path && location.pathname === menuItem.path;
                    return (
                        <ListItemButton
                            key={menuItem.text}
                            onClick={() => handleClickMenu(menuItem.path, menuItem.isModal, menuItem.isDownload)}
                            className={isSelected ? 'menuItemActive' : ''}
                            selected={isSelected}
                            disabled={['Live Monitoring', 'Devices'].includes(menuItem.text) && !selectedHouseId}
                        >
                            <ListItemIcon>
                                {React.cloneElement(menuItem.icon, {
                                    sx: isSelected ? { color: 'white' } : {},
                                    color: isSelected ? undefined : 'primary',
                                })}
                            </ListItemIcon>
                            <ListItemText
                                primary={menuItem.text}
                                sx={isSelected ? { color: 'white' } : {}}
                            />
                        </ListItemButton>
                    );
                })}
                {token && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListItemButton onClick={handleSignOut}>
                            <ListItemIcon>
                                <ExitToApp color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="Sign Out" />
                        </ListItemButton>
                    </>
                )}
            </List>
            <Box sx={{ mt: 'auto', px: 1.5, pb: 2 }}>
                <Box
                    onClick={() => handleClickMenu(null, false, true)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.primary.light + '15',
                        border: `2px solid ${theme.palette.primary.main}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            backgroundColor: theme.palette.primary.light + '25',
                            borderColor: theme.palette.primary.dark,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                        },
                    }}
                >
                    <AndroidIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            fontSize: '0.95rem',
                        }}
                    >
                        Download App
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default function Layout() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    // const { refreshHouses } = useRefreshHouses();
    // const { housesUpd } = useHousesContext();
    const { username, logout, token } = useUserContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md = tablet and below

    const [open, setOpen] = useState(false);
    const [loadingHouses, setLoadingHouses] = useState(true);
    const [zeroHouses, setZeroHouses] = useState(false);
    // Removed unused houseId state
    const [houses, setHouses] = useState([]);
    const [selectedHouseId, setSelectedHouseId] = useState(() => {
        // Load from localStorage on initial state
        return localStorage.getItem('selectedHouseId') || '';
    });
    const [modalOpen, setModalOpen] = useState(false);

    const lastMenuItemsRef = useRef([]);
    const { setHouseDetails } = useHouseDetails();

    const logoutAndRedirect = useLogoutAndRedirect(logout, navigate, lastMenuItemsRef, setHouses, setSelectedHouseId);

    // Handle redirect to live monitoring after login
    useEffect(() => {
        if (token && !loadingHouses && selectedHouseId && houses.length > 0) {
            // Check if we're on homepage and should redirect to live monitoring
            if (location.pathname === '/') {
                navigate(`/house/id/${selectedHouseId}/reports`);
            }
        }
    }, [token, loadingHouses, selectedHouseId, houses.length, location.pathname, navigate]);

    useEffect(() => {
        if (token) {
            setLoadingHouses(true);
            getAllHouses(token)
                .then(response => {
                    if (!response || response.length === 0) {
                        setZeroHouses(true);
                        setHouses([]);
                        setSelectedHouseId('');
                        localStorage.removeItem('selectedHouseId');
                    } else {
                        setZeroHouses(false);
                        setHouses(response);
                        setSelectedHouseId(prev => {
                            // Check if previous selection exists in the response
                            const storedId = localStorage.getItem('selectedHouseId');
                            const validId = storedId && response.some(h => h.id === storedId) ? storedId : null;
                            const finalId = validId || (prev && response.some(h => h.id === prev) ? prev : response[0].id);
                            // Save to localStorage
                            if (finalId) {
                                localStorage.setItem('selectedHouseId', finalId);
                            }
                            return finalId;
                        });
                    }
                })
                .catch(error => {
                    if (error && error.response && error.response.status === 401) {
                        logoutAndRedirect();
                    } else {
                        setZeroHouses(true);
                        setHouses([]);
                        setSelectedHouseId('');
                        localStorage.removeItem('selectedHouseId');
                    }
                })
                .finally(() => {
                    setLoadingHouses(false);
                });
        }
    }, [token, logoutAndRedirect]);

    // Refresh page when house changes and user is on a house-specific page
    useEffect(() => {
        if (!selectedHouseId) return;
        const reportsMatch = location.pathname.match(/^\/house\/id\/([^/]+)\/reports/);
        const devicesMatch = location.pathname.match(/^\/house\/id\/([^/]+)\/devices/);
        if (reportsMatch && reportsMatch[1] !== selectedHouseId) {
            navigate(`/house/id/${selectedHouseId}/reports`);
        } else if (devicesMatch && devicesMatch[1] !== selectedHouseId) {
            navigate(`/house/id/${selectedHouseId}/devices`);
        }
    }, [selectedHouseId, location.pathname, navigate]);

    // Update house details context when selected house changes
    useEffect(() => {
        if (selectedHouseId && houses && houses.length > 0) {
            const selectedHouse = houses.find(h => h.id === selectedHouseId);
            if (selectedHouse && setHouseDetails) {
                setHouseDetails({
                    houseId: selectedHouse.id,
                    houseName: selectedHouse.name,
                    houseAddress: selectedHouse.address,
                });
            }
        }
    }, [selectedHouseId, houses, setHouseDetails]);

    const handleDrawerOpen = useCallback(() => setOpen(true), []);
    const handleDrawerClose = useCallback(() => setOpen(false), []);

    // Handle download app functionality
    const handleDownload = useCallback(() => {
        axios({
            url: '/mobile/download',
            method: 'GET',
            responseType: 'blob',
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = 'ehs-app-release.apk';
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    a.remove();
                    window.URL.revokeObjectURL(url);
                }, 100);
            })
            .catch((error) => {
                alert('Failed to download the app. Please try again later.');
            });
    }, []);

    const handleClickMenu = useCallback((path, isModal = false, isDownload = false) => {
        if (isModal) {
            setModalOpen(true);
        } else if (isDownload) {
            handleDownload();
        } else {
            navigate(path);
        }
        setOpen(false);
    }, [navigate, handleDownload]);
    const handleSignOut = logoutAndRedirect;

    // Compute menu items based on current state, memoized for performance
    const computedMenuItems = React.useMemo(
        () => {
            if (!token || loadingHouses) {
                return [
                    { text: 'Homepage', icon: <DashboardIcon />, path: '/' },
                    { text: "I'm interested!", icon: <RocketLaunchIcon />, path: null, isModal: true },
                    { text: 'Sign In', icon: <LoginIcon />, path: '/signin' }
                ];
            }
            if (zeroHouses) {
                return [
                    { text: 'Homepage', icon: <DashboardIcon />, path: '/' },
                    { text: 'Add House', icon: <HouseIcon />, path: '/add/house' }
                ];
            }
            return [
                { text: 'Live Monitoring', icon: <MonitorIcon />, path: `/house/id/${selectedHouseId}/reports` },
                { text: 'Devices', icon: <DevicesIcon />, path: `/house/id/${selectedHouseId}/devices` }
            ];
        },
        [token, loadingHouses, zeroHouses, selectedHouseId]
    );

    // Sticky menu: use last valid menu items while loading for smooth UX
    let menuItems = computedMenuItems;
    if (!loadingHouses && token) {
        lastMenuItemsRef.current = menuItems;
    } else if (lastMenuItemsRef.current.length > 0) {
        menuItems = lastMenuItemsRef.current;
    }

    // Filter menu items for mobile navigation
    const filteredMenuItemsForMobile = React.useMemo(() => {
        let filtered = menuItems.filter(item => {
            // Only show navigation items for logged-in users with houses
            if (!token || loadingHouses || zeroHouses) return true; // Show all for non-logged-in
            if (houses && houses.length > 0) {
                // Show Live Monitoring and Devices
                return ['Live Monitoring', 'Devices'].includes(item.text);
            }
            return true;
        });

        // Add Download App for logged-in users with houses
        if (token && !loadingHouses && houses && houses.length > 0) {
            filtered = [...filtered, { text: 'Download App', icon: <AndroidIcon />, path: null, isDownload: true }];
        }

        return filtered;
    }, [menuItems, token, loadingHouses, zeroHouses, houses]);

    // Improved path matching: handle exact matches and route variations
    // Calculate index based on filtered items for mobile
    const currentNavIndex = filteredMenuItemsForMobile.findIndex(item => {
        if (!item.path) return false;

        // Special case for homepage: only match exactly '/'
        if (item.path === '/') {
            return location.pathname === '/';
        }

        // Exact match
        if (location.pathname === item.path) return true;

        // Match if current path starts with the menu item path (for nested routes)
        // But ensure it's not just a prefix match (e.g., '/signin' shouldn't match '/sign')
        if (location.pathname.startsWith(item.path + '/')) return true;

        // Match if current path starts with the menu item path without trailing slash
        const normalizedPath = item.path.replace(/\/$/, '');
        const normalizedLocation = location.pathname.replace(/\/$/, '');
        if (normalizedLocation === normalizedPath) return true;
        if (normalizedLocation.startsWith(normalizedPath + '/')) return true;

        return false;
    });

    // Map menuItems to BottomNavigationAction for mobile
    const bottomNavActions = filteredMenuItemsForMobile
        .map((item, idx) => {
            // Create shorter labels for mobile
            const getMobileLabel = (text) => {
                // if (text === 'Live Monitoring') return 'Monitoring';
                // if (text === 'Download App') return 'Download';
                return text;
            };

            if (item.text === 'Live Monitoring' && isMobile && houses && houses.length > 0) {
                return (
                    <BottomNavigationAction
                        key={item.text}
                        label={getMobileLabel(item.text)}
                        icon={item.icon}
                        onClick={() => handleClickMenu(item.path, item.isModal, item.isDownload)}
                        disabled={!selectedHouseId}
                        sx={{
                            color: theme.palette.text.secondary,
                            minWidth: 'auto',
                            px: { xs: 0.5, sm: 1 },
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                                '& .MuiBottomNavigationAction-label': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                                '& svg': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                );
            }
            if (item.text === 'Devices' && isMobile && houses && houses.length > 0) {
                return (
                    <BottomNavigationAction
                        key={item.text}
                        label={getMobileLabel(item.text)}
                        icon={item.icon}
                        onClick={() => handleClickMenu(item.path, item.isModal, item.isDownload)}
                        disabled={!selectedHouseId}
                        sx={{
                            color: theme.palette.text.secondary,
                            minWidth: 'auto',
                            px: { xs: 0.5, sm: 1 },
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                                '& .MuiBottomNavigationAction-label': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                                '& svg': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                );
            }
            if (item.text === 'Download App' && isMobile && houses && houses.length > 0) {
                return (
                    <BottomNavigationAction
                        key={item.text}
                        label={getMobileLabel(item.text)}
                        icon={item.icon}
                        onClick={() => handleClickMenu(item.path, item.isModal, item.isDownload)}
                        sx={{
                            color: theme.palette.text.secondary,
                            minWidth: 'auto',
                            px: { xs: 0.5, sm: 1 },
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                                '& .MuiBottomNavigationAction-label': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                                '& svg': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                );
            }
            return (
                <BottomNavigationAction
                    key={item.text}
                    label={getMobileLabel(item.text)}
                    icon={item.icon}
                    onClick={() => handleClickMenu(item.path, item.isModal, item.isDownload)}
                    sx={{
                        color: theme.palette.text.secondary,
                        minWidth: 'auto',
                        px: { xs: 0.5, sm: 1 },
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '&.Mui-selected': {
                            color: theme.palette.primary.main,
                            '& .MuiBottomNavigationAction-label': {
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                            },
                            '& svg': {
                                color: theme.palette.primary.main,
                            },
                        },
                    }}
                />
            );
        });

    // Remove Sign Out action for mobile

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden', maxWidth: '100vw' }}>
            <CssBaseline />
            <AppBar position="fixed" open={open}>
                <Toolbar sx={{
                    minHeight: { xs: '56px', sm: '64px' },
                    px: { xs: 1, sm: 2 },
                    overflow: 'hidden',
                    maxWidth: '100vw',
                }}>
                    {/* Hide burger icon on mobile */}
                    {!isMobile && (
                        <IconButton color="inherit" onClick={handleDrawerOpen} edge="start"
                            sx={{ mr: 2, ...(open && { display: 'none' }) }}>
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        overflow: 'hidden',
                        mr: { xs: 1, sm: 2 },
                    }}>
                        {/* Responsive: stack title and chip vertically on xs, inline on sm+ */}
                        <Box sx={{
                            display: { xs: 'block', sm: 'flex' },
                            flexDirection: { sm: 'row' },
                            alignItems: { sm: 'center' },
                            minWidth: 0,
                            overflow: 'hidden',
                            flex: 1,
                        }}>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.25rem' },
                                    fontWeight: 600,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: { xs: 'calc(100vw - 120px)', sm: 'none' },
                                }}
                            >
                                Energy Home System
                            </Typography>
                            {/* BETA chip below on xs, inline on sm+ */}
                            <Box sx={{
                                display: { xs: 'block', sm: 'inline-block' },
                                mt: { xs: 0.5, sm: 0 },
                                ml: { xs: 0, sm: 1 },
                                flexShrink: 0,
                            }}>
                                <Chip
                                    label="beta"
                                    size="small"
                                    color="secondary"
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: 1,
                                        textTransform: 'uppercase',
                                        color: 'white',
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    {username && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            gap: { xs: 0.5, sm: 1 },
                            ml: { xs: 0.5, sm: 1 },
                        }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' },
                                    fontWeight: 400,
                                    color: 'white',
                                    whiteSpace: 'nowrap',
                                    display: { xs: 'none', sm: 'block' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: { sm: '150px', md: '200px' },
                                }}
                            >
                                Welcome, {username}
                            </Typography>
                            <MenuButton
                                subLinks={[
                                    {
                                        title: 'Sign Out',
                                        icon: <LogoutOutlinedIcon color={'secondary'} />,
                                        signout: handleSignOut
                                    }
                                ]}
                            />
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            {/* Only render Drawer on desktop/tablet */}
            {!isMobile && (
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                        }
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    <DrawerHeader>
                        <IconButton onClick={handleDrawerClose}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>
                    <Divider />
                    <SidebarMenu
                        menuItems={menuItems}
                        houses={houses}
                        location={location}
                        handleClickMenu={handleClickMenu}
                        token={token}
                        handleSignOut={handleSignOut}
                        selectedHouseId={selectedHouseId}
                        setSelectedHouseId={setSelectedHouseId}
                        theme={theme}
                    />
                </Drawer>
            )}

            <Main open={!isMobile && open} sx={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                background: '#F6F7F9',
                overflowX: 'hidden',
                ...(isMobile && { paddingBottom: '56px' })
            }}>
                <DrawerHeader />
                <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                    <Outlet />
                </Box>
                {isMobile ? (
                    <>
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: theme.zIndex.appBar + 1,
                                backgroundColor: 'background.paper',
                                borderTop: '2px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                paddingX: { xs: 0.5, sm: 1 },
                                maxWidth: '100vw',
                                overflow: 'hidden',
                            }}
                        >
                            {/* House select dropdown */}
                            {houses && houses.length > 0 && (
                                <Box sx={{
                                    minWidth: { xs: 140, sm: 140 },
                                    maxWidth: { xs: 140, sm: 140 },
                                    mx: { xs: 0.5, sm: 1 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Select
                                        value={selectedHouseId}
                                        onChange={e => {
                                            const newHouseId = e.target.value;
                                            setSelectedHouseId(newHouseId);
                                            localStorage.setItem('selectedHouseId', newHouseId);
                                        }}
                                        fullWidth
                                        size="small"
                                        input={<OutlinedInput notched={false} sx={{ borderRadius: 2, fontWeight: 600, bgcolor: 'background.paper', boxShadow: 1, px: { xs: 1, sm: 1.5 }, py: 0.5 }} />}
                                        renderValue={selected => {
                                            const house = houses.find(h => h.id === selected);
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', minWidth: 0 }}>
                                                    <HouseIcon color="primary" sx={{ fontSize: { xs: 18, sm: 22 }, flexShrink: 0 }} />
                                                    <Typography
                                                        fontWeight={700}
                                                        sx={{
                                                            color: 'text.primary',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {house ? house.name : ''}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                        sx={{
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            boxShadow: 1,
                                            bgcolor: 'background.paper',
                                            mb: 0,
                                            minWidth: { xs: 140, sm: 160 },
                                            maxWidth: { xs: 140, sm: 160 },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    borderRadius: 2,
                                                    boxShadow: 3,
                                                    mt: 1,
                                                }
                                            }
                                        }}
                                    >
                                        {houses.map(house => (
                                            <MenuItem key={house.id} value={house.id} sx={{ py: 1.2, px: 2, borderRadius: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <HouseIcon color="primary" sx={{ fontSize: 20, mr: 1 }} />
                                                    <Typography fontWeight={600}>{house.name}</Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}
                            <BottomNavigation
                                showLabels
                                value={currentNavIndex >= 0 ? currentNavIndex : undefined}
                                onChange={(event, newValue) => {
                                    if (newValue === null || newValue === undefined) return;

                                    const selectedItem = filteredMenuItemsForMobile[newValue];
                                    if (selectedItem) {
                                        handleClickMenu(selectedItem.path, selectedItem.isModal, selectedItem.isDownload);
                                    }
                                }}
                                sx={{
                                    flex: 1,
                                    backgroundColor: 'transparent',
                                    borderTop: 'none',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    '& .MuiBottomNavigationAction-root': {
                                        minWidth: { xs: 'auto', sm: 64 },
                                        maxWidth: { xs: 'none', sm: 168 },
                                        padding: { xs: '6px 4px', sm: '6px 12px' },
                                    },
                                }}
                            >
                                {bottomNavActions}
                            </BottomNavigation>
                        </Box>
                        {/* Delete this after review */}
                        <Footer sx={{ position: 'relative', mt: 'auto' }} open={open}>
                            <FooterContent />
                        </Footer>
                    </>
                ) : (
                    <Footer sx={{ position: 'relative', mt: 'auto' }} open={open}>
                        <FooterContent />
                    </Footer>
                )}
            </Main>
            {/* Remove mobile popover logic for house selection */}

            <GetStartedModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </Box>
    );
}
